import { FREQUENCY_DISPLAYS } from '@/features/customization/constants/frequency.constant';
import { format, parse } from 'date-fns';

/**
 * Extracts the text outside the last parentheses group from a display string.
 * E.g. "OD (b/bf) (Once daily, before Breakfast)" => "OD (b/bf)"
 *      "OD (Once a day)" => "OD"
 *      "OD" => "OD"
 *      "Tab (Tablet(s))" => "Tab"
 */
export function extractShortDisplay(display: string): string {
  if (!display) return '';
  // Remove the last outermost parenthetical group (even if nested) at the end
  const str = display.trim();
  let depth = 0;
  for (let i = str.length - 1; i >= 0; i--) {
    if (str[i] === ')') depth++;
    if (str[i] === '(') depth--;
    if (depth === 0 && str[i] === '(') {
      // Remove from the space before '(' (if any) to the end
      const spaceIdx = i > 0 && str[i - 1] === ' ' ? i - 1 : i;
      return str.substring(0, spaceIdx).trim();
    }
  }
  return str;
}

/**
 * Extracts the text inside parentheses from a display string, or returns the original if not present.
 * E.g. "OD (Once a day)" => "Once a day"
 */
export const extractLongDisplay = (display: string): string => {
  if (!display) return '';
  const idx = display.indexOf('(');
  if (idx !== -1) {
    return display
      .substring(idx + 1, display.length - (display.endsWith(')') ? 1 : 0))
      .replace(')', '')
      .trim();
  }
  return display;
};

// Reformats the date to "MMMM d, yyyy. hh:mm a"
export const getParsedDate = (visitDisplay: string) => {
  const parts = visitDisplay?.split(' - ');
  const originalDateStr = parts?.[parts.length - 1]?.trim();
  const parsedDate = parse(originalDateStr, 'MM/dd/yyyy hh:mm a', new Date());
  return format(parsedDate, 'MMMM d, yyyy. hh:mm a');
};

export const countActiveDoseSlots = (dose: string) => {
  // fallback to 1 as non-zero default
  if (!dose || dose === '') return 1;

  const slots = dose.split('-');
  const result = slots.filter(slot => {
    const trimmed = slot.trim();
    return trimmed !== '' && trimmed !== '0';
  }).length;
  return result;
};
/**
 * Maps a dose notation (e.g., "1-0-1") to a frequency display string based on non-zero slots.
 * @param dose The dose notation string.
 * @returns The matched frequency display string or "Once a day" as fallback.
 */
export const getFrequencyByDoseNotation = (dose: string): string => {
  if (!dose || dose === 'PRN') return FREQUENCY_DISPLAYS.TAKE_AS_NEEDED;

  // Split by '-' and filter for non-zero, non-empty values

  const nonZeroSlotsCount = countActiveDoseSlots(dose);
  switch (nonZeroSlotsCount) {
    case 1:
      return FREQUENCY_DISPLAYS.ONCE_A_DAY;
    case 2:
      return FREQUENCY_DISPLAYS.TWICE_A_DAY;
    case 3:
      return FREQUENCY_DISPLAYS.THRICE_A_DAY;
    case 4:
      return FREQUENCY_DISPLAYS.FOUR_TIMES_A_DAY;
    default:
      return FREQUENCY_DISPLAYS.ONCE_A_DAY;
  }
};
